import React, { useEffect, useState, useRef } from 'react';

import requestClient from '../../utils/requestClient';

import FormInput from '../../components/Elements/Form/FormInput';
import SuccessMessage from '../../components/Elements/SuccessMessage';
import ErrorMessage from '../../components/Elements/ErrorMessage';

const Settings = ({ }) => {
    const [company, setCompany] = useState(null);
    const [locations, setLocations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [success, setSuccess] = useState(false);
    const [errors, setErrors] = useState(null);
    const [activeTab, setActiveTab] = useState('company')
    const [newLocationName, setNewLocationName] = useState('');
    const [newDepartmentName, setNewDepartmentName] = useState('');
    const companyNameRef = useRef(null)
    const companyAepPhoneNumberRef = useRef(null)
    const [logo, setLogo] = useState(null);

    useEffect(() => {
        async function getCompany() {
            const response = await requestClient().get('/api/v2/employer/company/')

            setCompany(response.data.data);
        }
        getCompany();
    }, []);

    useEffect(() => {
        async function getLocations() {
            try {
                const response = await requestClient().get('/api/v2/employer/locations/');
                setLocations(response.data.data);
            } catch (error) {
                console.error('Error fetching locations:', error);
            }
        }
        getLocations();
    }, []);

    useEffect(() => {
        async function getDepartments() {
            try {
                const response = await requestClient().get('/api/v2/employer/departments/');
                setDepartments(response.data.data);
            } catch (error) {
                console.error('Error fetching departments:', error);
            }
        }
        getDepartments();
    }, []);

    const fileChanged = (event) => {
        const reader = new FileReader()
        reader.readAsDataURL(event.target.files[0])
        reader.onload = () => {
            setLogo(reader.result)
        } 
    }

    async function handleAddLocation(event) {
        if (event.key === 'Enter' && newLocationName.trim()) {
            event.preventDefault();
            setErrors(null);
            try {
                const response = await requestClient().post('/api/v2/employer/locations/', {
                    name: newLocationName
                });
                if (response.status === 201) {
                    setLocations([...locations, response.data.data]);
                    setNewLocationName('');
                    setSuccess(true);
                    setTimeout(() => setSuccess(false), 3000);
                }
            } catch (error) {
                setErrors(error.response?.data?.error || 'Error adding location');
            }
        }
    }

    async function handleRemoveLocation(locationId) {
        setErrors(null);
        try {
            await requestClient().delete(`/api/v2/employer/locations/${locationId}/`);
            setLocations(locations.filter(loc => loc.id !== locationId));
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (error) {
            setErrors(error.response?.data?.error || 'Error removing location');
        }
    }

    async function handleAddDepartment(event) {
        if (event.key === 'Enter' && newDepartmentName.trim()) {
            event.preventDefault();
            setErrors(null);
            try {
                const response = await requestClient().post('/api/v2/employer/departments/', {
                    name: newDepartmentName
                });
                if (response.status === 201) {
                    setDepartments([...departments, response.data.data]);
                    setNewDepartmentName('');
                    setSuccess(true);
                    setTimeout(() => setSuccess(false), 3000);
                }
            } catch (error) {
                setErrors(error.response?.data?.error || 'Error adding department');
            }
        }
    }

    async function handleRemoveDepartment(departmentId) {
        setErrors(null);
        try {
            await requestClient().delete(`/api/v2/employer/departments/${departmentId}/`);
            setDepartments(departments.filter(dept => dept.id !== departmentId));
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (error) {
            setErrors(error.response?.data?.error || 'Error removing department');
        }
    }

    async function handleSubmit(event) {
        event.preventDefault()
        setSuccess(false)
        setErrors(null)

        await requestClient().patch('/api/v2/employer/company/', {
            company_name: companyNameRef.current.value,
            aep_phone_number: companyAepPhoneNumberRef.current.value,
            logo: logo
        }).then(response => {
            if(response.status === 200){
                setSuccess(true)
                setCompany(response.data.data);
            }
        })
        .catch(error => {
            setErrors(error.response.data.error)
        })
    }

    return (company ?
        <main>
            <section className="bg-white">
                <div className='container py-8 lg:py-12'>
                    <div className='flex items-center justify-between pb-8 mb-8'>
                        <h1>Settings</h1>
                    </div>
                    {success ? <SuccessMessage title="Success" text="Your changes have been saved" className="mb-4 sticky top-2" /> : null}
                    {errors ? <ErrorMessage title="Error" errors={errors} className="mb-4 sticky top-2" /> : null}
                    
                    {/* Tab Navigation */}
                    <div className='flex flex-col lg:flex-row mb-8 border-b'>
                        <button 
                            onClick={() => setActiveTab('company')}
                            className={`px-4 py-3 font-bold transition-colors ${
                                activeTab === 'company' 
                                    ? 'text-purple border-b-2 border-purple -mb-[2px]' 
                                    : 'text-gray-600 hover:text-purple'
                            }`}
                        >
                            Company
                        </button>
                        <button 
                            onClick={() => setActiveTab('locations')}
                            className={`px-4 py-3 font-bold transition-colors ${
                                activeTab === 'locations' 
                                    ? 'text-purple border-b-2 border-purple -mb-[2px]' 
                                    : 'text-gray-600 hover:text-purple'
                            }`}
                        >
                            Locations
                        </button>
                        <button 
                            onClick={() => setActiveTab('departments')}
                            className={`px-4 py-3 font-bold transition-colors ${
                                activeTab === 'departments' 
                                    ? 'text-purple border-b-2 border-purple -mb-[2px]' 
                                    : 'text-gray-600 hover:text-purple'
                            }`}
                        >
                            Departments
                        </button>
                    </div>
                    
                    {activeTab === 'company' && (
                        <form onSubmit={handleSubmit}>
                            <div className='grid gap-8 lg:gap-12 lg:grid-cols-12 mb-8'>
                                <div className='lg:col-span-6'>
                                    <div className='grid gap-6'>
                                        <div>
                                            {company.logo ? (<img src={company.logo} className='h-[100px] w-[100px] object-fit rounded-full mb-2' />) : null }
                                            <label className='block font-medium leading-tight mb-1'>Logo:</label>
                                            <input type="file" name="logo" label="Logo" onChange={fileChanged} />
                                        </div>
                                        <FormInput type="text" name="company_name" label="Name:" value={company.name} inputRef={companyNameRef} />
                                        <FormInput type="tel" name="aep_phone_number" label="AEP Phone Number:" 
                                            props={{'placeholder': '03332128303'}} value={company.aep_phone_number} inputRef={companyAepPhoneNumberRef} />
                                    </div>
                                 </div>
                            </div>
                            <button className="btn btn-purple block lg:inline-block lg:min-w-[250px]">UPDATE</button> 
                        </form>
                    )}
                    
                    {activeTab === 'locations' && (
                        <div>
                            <h2 className='text-2xl font-bold mb-6'>Locations</h2>
                            
                            <div className='max-w-2xl'>
                                <div className='bg-white'>
                                    <div className='flex flex-wrap gap-2 mb-4 min-h-[40px]'>
                                        {locations && locations.map((location) => (
                                            <div 
                                                key={location.id}
                                                className='inline-flex items-center gap-2 px-3 py-1 bg-purple text-white rounded-full text-sm font-medium'
                                            >
                                                <span>{location.name}</span>
                                                <button
                                                    type='button'
                                                    onClick={() => handleRemoveLocation(location.id)}
                                                    className='ml-1 hover:opacity-80 transition-opacity'
                                                    aria-label={`Remove ${location.name}`}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}

                                        <input
                                            type='text'
                                            value={newLocationName}
                                            onChange={(e) => setNewLocationName(e.target.value)}
                                            onKeyPress={handleAddLocation}
                                            placeholder='Add new...'
                                            className='inline-flex items-center gap-2 px-3 py-1 text-sm font-medium border border-gray-200 rounded-full focus:outline-none focus:ring-1 focus:ring-purple focus:border-transparent'
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {activeTab === 'departments' && (
                        <div>
                            <h2 className='text-2xl font-bold mb-6'>Departments</h2>
                            
                            <div className='max-w-2xl'>
                                <div className='bg-white'>
                                    <div className='flex flex-wrap gap-2 mb-4 min-h-[40px]'>
                                        {departments && departments.map((department) => (
                                            <div 
                                                key={department.id}
                                                className='inline-flex items-center gap-2 px-3 py-1 bg-purple text-white rounded-full text-sm font-medium'
                                            >
                                                <span>{department.name}</span>
                                                <button
                                                    type='button'
                                                    onClick={() => handleRemoveDepartment(department.id)}
                                                    className='ml-1 hover:opacity-80 transition-opacity'
                                                    aria-label={`Remove ${department.name}`}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}

                                        <input
                                            type='text'
                                            value={newDepartmentName}
                                            onChange={(e) => setNewDepartmentName(e.target.value)}
                                            onKeyPress={handleAddDepartment}
                                            placeholder='Add new...'
                                            className='inline-flex items-center gap-2 px-3 py-1 text-sm font-medium border border-gray-200 rounded-full focus:outline-none focus:ring-1 focus:ring-purple focus:border-transparent'
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </main>
    : null);
};

export default Settings;
